import { PixelStreaming } from '@epicgames-ps/lib-pixelstreamingfrontend-ue5.4'
import { AxiosResponse } from 'axios'
import { AuthedAxiosRequests } from 'features/auth'
import React, {
  createContext,
  Dispatch,
  RefObject,
  SetStateAction,
  useContext,
} from 'react'
import { Vector2 } from 'three'
import {
  G1N5,
  GeckoKestrelModel,
  KVAnnotation,
  KVCameraPose,
  KVCameraView,
  KVMarkup,
  KVMeasurement,
  KVNavigationMode,
  KVPhoto,
  KVPlaneIntersection,
  KVPolygon,
  KVPosition,
  KVRotation,
  KVSettings,
  KVSurfaceArea,
  RolodexEntryPatchJson,
} from 'types'

// Data used in JSON message handlers
export interface KestrelViewerMessageCallbackAPI {
  setViewerId: Dispatch<SetStateAction<string>>
  pixelStream: PixelStreaming | undefined
  videoParent: RefObject<HTMLDivElement>
  setSelectedMarkup: Dispatch<SetStateAction<KVMarkup | undefined>>
  mouseTrackerRef: RefObject<Vector2>
  setPopupMenuStartPosition: Dispatch<SetStateAction<Vector2>>
  annotations: KVAnnotation[] | undefined
  setAnnotations: Dispatch<SetStateAction<KVAnnotation[] | undefined>>
  settings: KVSettings | undefined
  setSettings: Dispatch<SetStateAction<KVSettings | undefined>>
  polygons: KVPolygon[] | undefined
  setPolygons: Dispatch<SetStateAction<KVPolygon[] | undefined>>
  measurements: KVMeasurement[] | undefined
  setMeasurements: Dispatch<SetStateAction<KVMeasurement[] | undefined>>
  surfaceAreas: KVSurfaceArea[] | undefined
  setSurfaceAreas: Dispatch<SetStateAction<KVSurfaceArea[] | undefined>>
  user: { name: string; email: string }
  rolodexPatch: (patchData: RolodexEntryPatchJson[]) => Promise<AxiosResponse>
  kestrelModel: GeckoKestrelModel | undefined
  planePosition?: KVPosition
  setPlanePosition?: Dispatch<SetStateAction<KVPosition | undefined>>
  planeRotation?: KVRotation
  setPlaneRotation?: Dispatch<SetStateAction<KVRotation | undefined>>
  planeIntersections: KVPlaneIntersection[] | undefined
  setPlaneIntersections: Dispatch<
    SetStateAction<KVPlaneIntersection[] | undefined>
  >
  photoReelOpen: boolean
  setPhotoReelOpen: Dispatch<SetStateAction<boolean>>
  selectedPhoto: KVPhoto | null
  setSelectedPhoto: Dispatch<SetStateAction<KVPhoto | null>>
  photos: KVPhoto[]
  setPhotos: Dispatch<SetStateAction<KVPhoto[]>>
  filteredPhotos: KVPhoto[] | null
  setFilteredPhotos: Dispatch<SetStateAction<KVPhoto[] | null>>
  cameraViews: KVCameraView[] | undefined
  setCameraViews: Dispatch<SetStateAction<KVCameraView[] | undefined>>
  navigationMode: KVNavigationMode
  setNavigationMode: Dispatch<SetStateAction<KVNavigationMode>>
  authorizedAxios: AuthedAxiosRequests
  kestrelApiUrl: string
  filteringPhotos: boolean
  setFilteringPhotos: Dispatch<SetStateAction<boolean>>
}

// Data available to client-side Kestrel Viewer UI
export type KestrelViewerContextType = {
  sendCommand: (json: string | object) => void
  selectedMode: string
  setSelectedMode: Dispatch<SetStateAction<string>>
  selectedMarkup: KVMarkup | undefined
  setSelectedMarkup: Dispatch<SetStateAction<KVMarkup | undefined>>
  popupMenuStartPosition: Vector2
  helperTextVisible: boolean
  setHelperTextVisible: Dispatch<SetStateAction<boolean>>
  settingsOpen: boolean
  setSettingsOpen: Dispatch<SetStateAction<boolean>>
  settings: KVSettings | undefined
  setSettings: Dispatch<SetStateAction<KVSettings | undefined>>
  is2DPlaneModeActive: boolean
  setIs2DPlaneModeActive: Dispatch<SetStateAction<boolean>>
  planePosition?: KVPosition
  setPlanePosition?: Dispatch<SetStateAction<KVPosition | undefined>>
  planeRotation?: KVRotation
  setPlaneRotation?: Dispatch<SetStateAction<KVRotation | undefined>>
  measurementType: string
  setMeasurementType: Dispatch<SetStateAction<string>>
  planeIntersections: KVPlaneIntersection[] | undefined
  setPlaneIntersections: Dispatch<
    SetStateAction<KVPlaneIntersection[] | undefined>
  >
  cameraViews: KVCameraView[] | undefined
  setCameraViews: Dispatch<SetStateAction<KVCameraView[] | undefined>>
  photoReelOpen: boolean
  setPhotoReelOpen: Dispatch<SetStateAction<boolean>>
  g1n5FormOpen: boolean
  setG1n5FormOpen: Dispatch<SetStateAction<boolean>>
  g1n5: G1N5 | undefined
  setG1n5: Dispatch<SetStateAction<G1N5 | undefined>>
  selectedPhoto: KVPhoto | null
  setSelectedPhoto: Dispatch<SetStateAction<KVPhoto | null>>
  photos: KVPhoto[]
  setPhotos: Dispatch<SetStateAction<KVPhoto[]>>
  filteredPhotos: KVPhoto[] | null
  setFilteredPhotos: Dispatch<SetStateAction<KVPhoto[] | null>>
  photosLoading: boolean
  photosError: Error | null
  containerRef: RefObject<HTMLDivElement>
  surfaceAreas: KVSurfaceArea[] | undefined
  setSurfaceAreas: Dispatch<SetStateAction<KVSurfaceArea[] | undefined>>
  navigationMode: KVNavigationMode
  setNavigationMode: Dispatch<SetStateAction<KVNavigationMode>>
  kestrelModel: GeckoKestrelModel | undefined
  pixelStreamingURL: string
  setPixelStreamingURL: Dispatch<SetStateAction<string>>
  setSendKVCommand: Dispatch<
    SetStateAction<((json: object | string) => void) | undefined>
  >
  setPopupMenuStartPosition: Dispatch<SetStateAction<Vector2>> | undefined
  cameraPoses: KVCameraPose[] | null
  setCameraPoses: Dispatch<SetStateAction<KVCameraPose[] | null>>
  filteringPhotos: boolean
  setFilteringPhotos: Dispatch<SetStateAction<boolean>>

  updateAnnotation: (
    annotation: KVAnnotation,
    jsonField: string | string[],
    setter: (a: KVAnnotation) => KVAnnotation,
    v: unknown | unknown[]
  ) => void

  updatePolygon: (
    annotation: KVPolygon,
    jsonField: string | string[],
    setter: (a: KVPolygon) => KVPolygon,
    v: unknown | unknown[]
  ) => void

  updateMeasurement: (
    measurement: KVMeasurement,
    jsonField: string | string[],
    setter: (a: KVMeasurement) => KVMeasurement,
    v: string | boolean | (string | boolean)[]
  ) => void
}

export const KestrelViewerContext = createContext<
  KestrelViewerContextType | undefined
>(undefined)

export const KestrelViewerProvider: React.FC<{
  children: React.ReactNode
  value: KestrelViewerContextType
}> = ({ children, value }) => {
  return (
    <KestrelViewerContext.Provider value={value}>
      {children}
    </KestrelViewerContext.Provider>
  )
}

export const useKestrelViewerContext = () => {
  const context = useContext(KestrelViewerContext)
  if (!context)
    throw new Error(
      'useKestrelViewerContext() must be used within KestrelViewerProvider'
    )
  return context
}
